// The app's navigation: a full-height sidebar (account selector, Home and the daily
// routine, the Journal pages) that folds into an icon rail on desktop and slides out
// as a drawer on phones, plus a slim top bar.
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Logo } from './Brand';
import { accountStatus } from './balance';
import { maskNumber } from './Accounts';
import { LanguageSwitch, useI18n } from './i18n';
import { Icon } from './Icons';
import { FoundingBadge } from './Founding';

const JOURNAL_OPEN_KEY = 'mm-nav-journal';

export const HOME_PAGES = [
  { view: 'home', to: '/app/home', icon: 'home', label: 'Home' },
  { view: 'morning', to: '/app/morning', icon: 'sun', label: 'Morning prep' },
  { view: 'session', to: '/app/session', icon: 'play', label: 'Live session' },
  { view: 'reflection', to: '/app/reflection', icon: 'moon', label: 'End-of-day reflection' },
  { view: 'market', to: '/app/market-calendar', icon: 'calendar', label: 'Market events' },
];

export const JOURNAL_PAGES = [
  { view: 'dashboard', to: '/app/dashboard', icon: 'grid', label: 'Dashboard' },
  { view: 'journal', to: '/app', icon: 'list', label: 'Trades', end: true },
  { view: 'accounts', to: '/app/accounts', icon: 'wallet', label: 'Accounts' },
  { view: 'insights', to: '/app/insights', icon: 'insights', label: 'Insights' },
  { view: 'playbook', to: '/app/playbook', icon: 'book', label: 'Playbook' },
  { view: 'goals', to: '/app/goals', icon: 'target', label: 'Goals' },
  { view: 'notebook', to: '/app/notebook', icon: 'note', label: 'Notebook' },
];

// The new My Trading Journey: one part per question, in the order a trader asks them.
function JOURNEY_PARTS() {
  return [
    { view: 'jmoney', to: '/app/journey/profitable', icon: 'money', label: 'Am I profitable?', feature: 'journey' },
    { view: 'jbetter', to: '/app/journey/better', icon: 'chart', label: 'Am I getting better?', feature: 'journey' },
    { view: 'jgrowth', to: '/app/journey/edge-milestones', icon: 'target', label: 'My edge & milestones', feature: 'journey' },
    { view: 'jidentity', to: '/app/journey/identity', icon: 'star', label: 'Who I’m becoming', feature: 'identity' },
  ];
}
export const JOURNEY_PART_VIEWS = JOURNEY_PARTS().map((p) => p.view);

// My Trading Journey: a sidebar group with its three parts; Coach MARK sits below it. Each page belongs to a feature
// that Admin → Features opens to members (user.features); until then only admins see it, in gold.
export const JOURNEY_PAGES = [
  { view: 'mark', to: '/app/mark', icon: 'chat', label: 'Coach MARK', feature: 'mark' },
  ...JOURNEY_PARTS(),
];
export const featureOpen = (user, feature) => Boolean(user && user.features && user.features[feature]);
export const canSeeView = (user, view) => {
  const page = JOURNEY_PAGES.find((p) => p.view === view);
  return !page || Boolean(user && user.isAdmin) || featureOpen(user, page.feature);
};

// Matrix Academy (Academy.js): the course, Chart Lab and Edge Lab, in their own section with their
// own top bar. Admin-only preview for now.
export const ACADEMY_PAGE = { view: 'academy', to: '/academy', icon: 'book', label: 'Matrix Academy', adminPreview: true };
export const COMPARE_PAGE = { view: 'compare', to: '/app/compare', icon: 'compare', label: 'Compare', adminPreview: true };

function readOpen() {
  try {
    return localStorage.getItem(JOURNAL_OPEN_KEY) !== 'closed';
  } catch {
    return true;
  }
}

// The account picker at the top of the sidebar: the account(s) shown, and a list of every
// account with its balance and distance from max loss. Clicking a name shows just that
// account; the checkboxes combine several. `selected` is 'all', an id or a list of ids.
// The accounts picker in the toolbar: "All accounts", one account's name, or "Name +2". The menu has
// All accounts and a search box on top, then every account with a checkbox to combine them (a
// click on the name shows only that account), then Manage accounts.
export function AccountSelect({ accounts, trades, selected, onSelect, onManage, className = '' }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);
  const shown = accounts.filter((a) => !a.archived);
  const ids = selected === 'all' ? [] : Array.isArray(selected) ? selected : [selected];
  const picked = shown.filter((a) => ids.includes(a._id));
  const all = !picked.length || picked.length === shown.length;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useEffect(() => { if (!open) setQ(''); }, [open]);

  function pick(id) {
    onSelect(id);
    setOpen(false);
  }

  // Ticks or unticks one account, keeping the menu open. With all accounts showing, every box is
  // ticked; unticking leaves the rest. None or all ticked means All accounts.
  function toggle(id) {
    const base = all ? shown.map((a) => a._id) : ids;
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    onSelect(!next.length || next.length === shown.length ? 'all' : next.length === 1 ? next[0] : next);
  }

  const label = all ? t('All accounts') : picked.length === 1 ? picked[0].name : `${picked[0].name} +${picked.length - 1}`;
  const query = q.trim().toLowerCase();
  const list = shown.filter((a) => !query || `${a.name} ${a.broker || ''} ${a.platform || ''} ${a.firm || ''}`.toLowerCase().includes(query));

  return (
    <div className={`account-select ${className}`} ref={box}>
      <button type="button" className="account-select-button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        title={all ? t('All accounts') : picked.map((a) => a.name).join(', ')}>
        <span className="account-select-text"><span className="account-select-name">{label}</span></span>
        <span className="tb-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="account-select-menu as-menu" role="listbox" aria-multiselectable="true" aria-label={t('Accounts')}>
          <div className="as-top">
            <button type="button" role="option" aria-selected={all} className={`as-all${all ? ' on' : ''}`} onClick={() => pick('all')}>
              <span className="as-radio" aria-hidden="true" />{t('All accounts')}
            </button>
            {shown.length > 4 && (
              <input type="search" className="as-search" placeholder={t('Search by name or broker')} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            )}
          </div>
          <span className="as-head">{t('My accounts')}</span>
          <div className="as-list">
            {list.map((a) => {
              const on = all || ids.includes(a._id);
              const status = accountStatus(a, trades.filter((x) => x.accountId === a._id));
              const kind = status && status.risk && status.risk !== 'safe' ? 'watch' : (a.type === 'Evaluation' || a.type === 'Funded' || a.profitTarget || a.maxLossLimit) ? 'prop' : 'personal';
              return (
                <div key={a._id} className={`as-row${on ? ' on' : ''}`}>
                  <label className="as-check">
                    <input type="checkbox" checked={on} onChange={() => toggle(a._id)} aria-label={t('Include {name}', { name: a.name })} />
                  </label>
                  <button type="button" role="option" aria-selected={!all && ids.length === 1 && ids[0] === a._id} className="as-name" onClick={() => pick(a._id)} title={t('Show only {name}', { name: a.name })}>
                    <span className={`dov-dot ${kind}`} aria-hidden="true" />
                    <span>{a.name}</span>
                    {a.externalId && <small>{maskNumber(a.externalId)}</small>}
                  </button>
                </div>
              );
            })}
            {!list.length && <p className="as-empty">{t('No account matches that search.')}</p>}
          </div>
          <button type="button" className="as-manage" onClick={() => { setOpen(false); onManage(); }}>{t('Manage accounts')}</button>
        </div>
      )}
    </div>
  );
}

function NavItem({ page, onNavigate, collapsed, main = false }) {
  const { t } = useI18n();
  return (
    <li>
      <NavLink to={page.to} end={page.end} onClick={onNavigate} title={collapsed ? t(page.label) : undefined}
        className={({ isActive }) => `nav-item${main ? ' nav-item-main' : ''}${page.adminPreview ? ' admin-preview' : ''}${isActive ? ' active' : ''}`}>
        <Icon name={page.icon} />
        <span className="nav-label">{t(page.label)}</span>
      </NavLink>
    </li>
  );
}

export function AppSidebar({ view, user, accounts, trades, selected, onSelect, onManage, collapsed, onToggleCollapse, drawerOpen, onCloseDrawer }) {
  const { t } = useI18n();
  const inJournal = JOURNAL_PAGES.some((p) => p.view === view) || view === 'compare' || view === 'account-new';
  const [journalOpen, setJournalOpen] = useState(readOpen);

  // Opening a Journal page always shows its menu.
  useEffect(() => {
    if (inJournal) setJournalOpen(true);
  }, [inJournal]);

  function toggleJournal() {
    setJournalOpen((o) => {
      try {
        localStorage.setItem(JOURNAL_OPEN_KEY, o ? 'closed' : 'open');
      } catch {
        // Not remembered in private mode; that's fine.
      }
      return !o;
    });
  }

  const showJournal = journalOpen || collapsed;
  const journeyPages = JOURNEY_PAGES.filter((p) => canSeeView(user, p.view)).map((p) => ({ ...p, adminPreview: !featureOpen(user, p.feature) }));
  const markPage = journeyPages.find((p) => p.view === 'mark');
  const partPages = journeyPages.filter((p) => JOURNEY_PART_VIEWS.includes(p.view));

  return (
    <aside className={`app-sidebar${collapsed ? ' is-collapsed' : ''}${drawerOpen ? ' is-open' : ''}`} aria-label={t('Main navigation')}>
      <div className="app-sidebar-head">
        <Link to="/app/home" className="app-brand" onClick={onCloseDrawer}>
          <Logo />
          <span className="app-brand-text">
            <span className="brand-name">Market Matrix</span>
            <span className="brand-tag">{t('Trading Journal')}</span>
          </span>
        </Link>
        <button type="button" className="icon-button app-sidebar-toggle" onClick={onToggleCollapse}
          aria-label={collapsed ? t('Expand the menu') : t('Collapse the menu')} title={collapsed ? t('Expand the menu') : t('Collapse the menu')}>
          <Icon name="sidebar" />
        </button>
        <button type="button" className="icon-button app-drawer-close" onClick={onCloseDrawer} aria-label={t('Close the menu')}>
          <Icon name="close" />
        </button>
      </div>

      <nav className="app-nav" aria-label={t('Pages')}>
        <ul className="nav-group">
          {HOME_PAGES.map((p, i) => <NavItem key={p.view} page={p} main={i === 0} collapsed={collapsed} onNavigate={onCloseDrawer} />)}
        </ul>

        <div className="nav-divider" />

        <button type="button" className={`nav-group-head${inJournal ? ' has-active' : ''}`} aria-expanded={showJournal} onClick={toggleJournal}
          title={collapsed ? t('Journal') : undefined}>
          <Icon name="journal" />
          <span className="nav-label">{t('Journal')}</span>
          <span className={`nav-chevron${showJournal ? ' open' : ''}`}><Icon name="chevron" /></span>
        </button>
        {showJournal && (
          <ul className="nav-group nav-sub">
            {JOURNAL_PAGES.map((p) => <NavItem key={p.view} page={p} collapsed={collapsed} onNavigate={onCloseDrawer} />)}
          </ul>
        )}

        {user.isAdmin && !user.isDemo && (
          <>
            <div className="nav-divider" />
            <ul className="nav-group">
              <NavItem page={ACADEMY_PAGE} collapsed={collapsed} onNavigate={onCloseDrawer} />
            </ul>
          </>
        )}

        {partPages.length > 0 && (
          <>
            <div className="nav-divider" />
            <p className={`nav-group-head nav-group-label${partPages.some((p) => p.adminPreview) ? ' admin-preview' : ''}`} title={collapsed ? t('My Trading Journey') : undefined}>
              <Icon name="chart" />
              <span className="nav-label">{t('My Trading Journey')}</span>
            </p>
            <ul className="nav-group nav-sub">
              {partPages.map((p) => <NavItem key={p.view} page={p} collapsed={collapsed} onNavigate={onCloseDrawer} />)}
            </ul>
          </>
        )}
        {markPage && (
          <>
            <div className="nav-divider" />
            <ul className="nav-group">
              <NavItem page={markPage} collapsed={collapsed} onNavigate={onCloseDrawer} />
            </ul>
          </>
        )}

        <div className="nav-divider" />

        <ul className="nav-group nav-bottom">
          {user.plan && !user.isDemo && (user.plan.paywall || user.isAdmin) && (
            <NavItem page={{ view: 'plans', to: '/app/plans', icon: 'star', label: user.plan.plan === 'pro' ? 'Plans' : 'Upgrade', adminPreview: !user.plan.paywall }} collapsed={collapsed} onNavigate={onCloseDrawer} />
          )}
          <NavItem page={{ view: 'settings', to: '/app/settings', icon: 'gear', label: 'Settings' }} collapsed={collapsed} onNavigate={onCloseDrawer} />
          <NavItem page={{ view: 'help', to: '/contact', icon: 'help', label: 'Help' }} collapsed={collapsed} onNavigate={onCloseDrawer} />
          {user.isAdmin && <NavItem page={{ view: 'admin', to: '/admin', icon: 'admin', label: 'Admin' }} collapsed={collapsed} onNavigate={onCloseDrawer} />}
        </ul>
      </nav>
    </aside>
  );
}

// The slim bar above every page: the menu button on phones, where you are, and links.
export function AppTopBar({ view, user, onOpenDrawer, onLogout, onExitDemo }) {
  const { t, lang } = useI18n();
  const page = [...HOME_PAGES, ...JOURNAL_PAGES, ...JOURNEY_PAGES, COMPARE_PAGE].find((p) => p.view === view);
  const section = JOURNAL_PAGES.some((p) => p.view === view) || view === 'compare' || view === 'account-new' ? t('Journal')
    : JOURNEY_PAGES.some((p) => p.view === view) ? t('My Trading Journey') : view === 'settings' || view === 'plans' ? t('Account') : t('Your day');
  return (
    <header className="app-topbar">
      <div className="app-topbar-left">
        <button type="button" className="icon-button app-menu-button" onClick={onOpenDrawer} aria-label={t('Open the menu')}>
          <Icon name="menu" />
        </button>
        <span className="app-crumb">
          <span>{section}</span>
          <span aria-hidden="true">/</span>
          {view === 'account-new' && <><Link to="/app/accounts">{t('Accounts')}</Link><span aria-hidden="true">/</span></>}
          <strong>{page ? t(page.label) : view === 'settings' ? t('Settings') : view === 'plans' ? t('Plans') : view === 'account-new' ? t('Add account') : ''}</strong>
        </span>
      </div>
      <div className="app-topbar-right">
        {user.isAdmin && !user.isDemo && <Link to="/academy" className="header-link academy-link admin-preview">{t('Matrix Academy')}</Link>}
        <Link to={lang === 'es' ? '/es/blog' : '/blog'} className="header-link hide-narrow">{t('Blog')}</Link>
        <Link to={lang === 'es' ? '/es/tools' : '/tools'} className="header-link hide-narrow">{t('Free tools')}</Link>
        <LanguageSwitch />
        <span className="app-user" title={user.name}>
          <span className="app-user-initial" aria-hidden="true">{(user.name || '?').trim().charAt(0).toUpperCase()}</span>
          <span className="app-user-name hide-narrow">{user.name}</span>
        </span>
        <FoundingBadge user={user} />
        <button type="button" className="secondary small" onClick={user.isDemo ? onExitDemo : onLogout}>
          {user.isDemo ? t('Exit demo') : t('Log out')}
        </button>
      </div>
    </header>
  );
}
